import React from 'react';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import AgricultureOutlinedIcon from '@mui/icons-material/AgricultureOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';

import orderDeskManifest from './order-desk/manifest';
import catalogManifest from './catalog/manifest';
import paymentsManifest from './payments/manifest';
import leadsManifest from './leads/manifest';
import peopleManifests from './people/manifest';
import farmerManifest from './farmer/manifest';
import conversationManifests from './conversation/manifest';
import farmOpsManifests from './farm/manifest';
import programsManifests from './programs/manifest';
import homeManifest from './home/manifest';
import insightsManifest from './insights/manifest';
import adminManifests from './admin/manifest';
import featureMgmtManifest from './platform-admin/manifest';
import rbacManifest from './rbac/manifest';
import dealersManifest from './dealers/manifest';
import inboxManifest from './inbox/manifest';

/**
 * The 7-capability-module registry (CRM_CONSOLIDATION_PLAN §2 target IA).
 *
 * A module groups feature manifests; the sidebar renders one heading per module
 * with its granted children beneath (module visible when ANY child feature is
 * granted; null permissions — admin/super-admin — see everything). Routes stay
 * flat: every URL keeps working unchanged.
 */
export const MODULES = [
    {
        id: 'people',
        title: 'People',
        icon: <PeopleAltOutlinedIcon />,
        features: [...peopleManifests, farmerManifest, leadsManifest],
    },
    {
        id: 'conversations',
        title: 'Conversations',
        icon: <ForumOutlinedIcon />,
        features: [inboxManifest, ...conversationManifests, dealersManifest],
    },
    {
        id: 'commerce',
        title: 'Commerce',
        icon: <StorefrontOutlinedIcon />,
        features: [orderDeskManifest, catalogManifest],
    },
    {
        id: 'farm-ops',
        title: 'Farm Ops',
        icon: <AgricultureOutlinedIcon />,
        features: [...farmOpsManifests],
    },
    {
        id: 'programs',
        title: 'Programs',
        icon: <AssignmentOutlinedIcon />,
        features: [...programsManifests],
    },
    {
        id: 'insights',
        title: 'Insights',
        icon: <InsightsOutlinedIcon />,
        // Home sits here rather than in a module of its own: a one-item "Home"
        // group would render a redundant heading, and Home + the 360 dashboard are
        // the same kind of surface (overview).
        features: [homeManifest, insightsManifest],
    },
    {
        id: 'platform-admin',
        title: 'Platform Admin',
        icon: <AdminPanelSettingsOutlinedIcon />,
        // Payments = PhyFarm's own subscription/licensing billing (not a Zoho order
        // ledger), so it lives here with the other admin surfaces, not under Commerce.
        features: [rbacManifest, ...adminManifests, featureMgmtManifest, paymentsManifest],
    },
];

// Flat route list — App.js mounts these; paths are unchanged from the pre-module IA.
export const moduleRoutes = MODULES.flatMap((m) => m.features.flatMap((f) => f.routes || []));

// Sidebar model: one group per module, each item carrying its gating featureName.
export const moduleNavGroups = MODULES.map((m) => ({
    id: m.id,
    title: m.title,
    icon: m.icon,
    items: m.features.flatMap((f) =>
        (f.sidebarItems || []).map((item) => ({ ...item, featureName: f.featureName }))
    ),
}));
